import { Breadcrumbs, Link, styled } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import Layout from '../components/Layout';
import Selector from '../components/pages/trackerPage/selector';
import SliderCard from '../components/pages/trackerPage/sliderCard';
import Timeline from '../components/pages/trackerPage/timeline';
import useElementOnScreen from '../components/pages/trackerPage/useElementOnScreen';
import { kssTrackerDataAtom, kssTrackerDataSelector, timeLineDataAtom } from '../recoil/atoms/trackerPageAtoms';
import { ReactComponent as DotDivider } from '../images/dot_divider.svg';
import { bgColors, colors } from '../styles/globalStyles';
import FeedbackModal from '../components/feedbackModal';
import RateModal from '../components/modals/rateModal';
import textData from '../data/data.json';
import {
    kssDataAtom,
    userForAdminPanelAtom,
    userAtom,
    userKssTrackerDataForAdminPanelAtom,
} from '../recoil/atoms/userAtom';
import { selectedLanguageAtom } from '../recoil/atoms/languageAtom';

const TrackerWrap = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'flex-start',
    gap: 17,
});

export const getColorType = (type) => {
    if (!type) return null;
    var lowerCaseType = type.toLowerCase();

    switch (lowerCaseType) {
        case 'keep':
            return bgColors.purpleBlue;
        case 'stop':
            return bgColors.orangePink;
        case 'start':
            return bgColors.green;
    }
};

const Header = styled('div')({
    padding: '0 10px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
});

const Slider = styled('div')({
    height: '75svh',
    display: 'flex',
    gap: 6,
    overflow: 'auto',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    margin: '0 -17px',

    // 'div:first-child': {
    //     marginLeft: 17,
    // },

    // '&:nth-last-of-type(1)': {
    //     paddingRight: '17px',
    // },
});

const getTimelineKSSByDay = (timeLineData, cardId) => {
    const selectedDayKSS = timeLineData.periods.reduce((acc, period) => {
        if (acc) return acc;

        return period.days.reduce((innerAcc, day) => {
            if (innerAcc) return innerAcc;

            if (day.date.startsWith(cardId)) {
                return day.scores;
            }

            return null;
        }, null);
    }, null);

    return selectedDayKSS;
};

const TrackerPage = () => {
    const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.7,
    };
    
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { trackerPage } = textData[selectedLanguage];

    const navigate = useNavigate();
    const sliderRef = useRef(null);
    const [selectedType, setSelectedType] = useState(null);
    const [kssTrackerData2, setKssTrackerData] = useRecoilState(kssTrackerDataAtom);
    const setData = useSetRecoilState(kssTrackerDataSelector);
    const [open, setOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);

    const [keepRef, isKeepVisible] = useElementOnScreen(options);
    const [stopRef, isStopVisible] = useElementOnScreen(options);
    const [startRef, isStartVisible] = useElementOnScreen(options);
    const [canScore, setCanScore] = useState(false);
    const [selectedDateKSS, setSelectedDateKSS] = useState(null);

    const userInfo = useRecoilValue(userAtom);
    const timeLineData = useRecoilValue(timeLineDataAtom);

    const userTrackerForAdminPanel = useRecoilValue(userKssTrackerDataForAdminPanelAtom);
    const userForAdminPanel = useRecoilValue(userForAdminPanelAtom);
    const [userKssData, setUserKssData] = useRecoilState(kssDataAtom);

    const userIdForAdminPanel = localStorage.getItem('user-id-for-admin-panel');

    const kssTrackerData = userIdForAdminPanel ? userTrackerForAdminPanel : userKssData;
    const userData = userIdForAdminPanel ? userForAdminPanel : userInfo;

    const handleScoreChanged = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (selectedType) {
            const targetIndex = kssTrackerData.findIndex((i) => i?.id === selectedType.id);
            const newData = Object.assign([], kssTrackerData);
            newData[targetIndex] = selectedType.score?.value === -1 ? { ...selectedType, score: null } : selectedType;
            setData(newData);
        }
    }, [selectedType]);

    useEffect(() => {
        const selectedDayKSS = getTimelineKSSByDay(timeLineData, new Date().toISOString().substring(0, 10));
        setSelectedDateKSS(selectedDayKSS);
    }, []);

    if (kssTrackerData.length === 0) return <></>;

    const kssVisible = (data) => {
        const visibleKSSList = data.filter((item) => {
            return item && !item.skip;
        });

        return visibleKSSList;
    };

    function setSelectedKss(behaviorData) {
        setSelectedType(behaviorData);

        // Обновляю текст обратной связи в текущих поведениях
        setKssTrackerData((prev) => {
            return prev.map((behavior) =>
                behavior.id === behaviorData.id
                    ? { ...behavior, feedback_text: behaviorData.feedback_text, score: behaviorData.score }
                    : behavior,
            );
        });

        setUserKssData((prev) =>
            prev.map((prevKssItem) => {
                if (prevKssItem.id !== behaviorData.behavior) {
                    return prevKssItem;
                }

                prevKssItem = { ...prevKssItem, score: behaviorData.score, feedback_text: behaviorData.feedback_text };
                return prevKssItem;
            }),
        );

        const newSelectedDateKSS = { ...selectedDateKSS };

        if (!newSelectedDateKSS.keep && behaviorData.status === 'keep') {
            newSelectedDateKSS.keep = { ...behaviorData.score, feedback_text: behaviorData.feedback_text };
        } else if (newSelectedDateKSS.keep?.id === behaviorData.score.id) {
            newSelectedDateKSS.keep = { ...newSelectedDateKSS.keep, result: behaviorData.score.result };
        }

        if (!newSelectedDateKSS.stop && behaviorData.status === 'stop') {
            newSelectedDateKSS.stop = { ...behaviorData.score, feedback_text: behaviorData.feedback_text };
        } else if (newSelectedDateKSS.stop?.id === behaviorData.score.id) {
            newSelectedDateKSS.stop = { ...newSelectedDateKSS.stop, result: behaviorData.score.result };
        }

        if (!newSelectedDateKSS.start && behaviorData.status === 'start') {
            newSelectedDateKSS.start = { ...behaviorData.score, feedback_text: behaviorData.feedback_text };
        } else if (newSelectedDateKSS.start?.id === behaviorData.score.id) {
            newSelectedDateKSS.start = { ...newSelectedDateKSS.start, result: behaviorData.score.result };
        }

        setSelectedDateKSS(newSelectedDateKSS);
    }

    function handleDateBlockClick(cardId) {
        const selectedDayKSS = getTimelineKSSByDay(timeLineData, cardId);
        setSelectedDateKSS(selectedDayKSS);
    }

    return (
        <Layout link={'/main'} style={{ justifyContent: 'space-between', padding: '0 17px', marginBottom: 0 }}>
            <TrackerWrap>
                <Header>
                    <Breadcrumbs separator={<DotDivider style={{ width: 2, height: 2 }} />}>
                        <Link
                            sx={{ color: colors.darkBlue, opacity: 0.4, cursor: 'pointer' }}
                            onClick={() => navigate('/main')}
                        >
                            {trackerPage.breadcrumbs.previous}
                        </Link>
                        <Link>{trackerPage.breadcrumbs.current}</Link>
                    </Breadcrumbs>
                    <Selector
                        ioVars={{ isKeepVisible, isStopVisible, isStartVisible }}
                        sliderRef={sliderRef}
                        kssVisible={kssVisible(kssTrackerData)}
                    />
                </Header>
                <Slider ref={sliderRef}>
                    {kssTrackerData.map((item) => {
                        const type = item?.status;
                        let ref;
                        let scores = selectedDateKSS?.[type];

                        switch (type) {
                            case 'keep':
                                ref = keepRef;
                                break;
                            case 'stop':
                                ref = stopRef;
                                break;
                            case 'start':
                                ref = startRef;
                        }

                        return (
                            <SliderCard
                                key={`tracker-page-slide-${type}`}
                                style={{
                                    paddingBottom: '50%',
                                    // marginLeft: index === 0 ? 17 : 0,
                                }}
                                kssTrackerCardData={item}
                                reference={ref}
                                setOpen={setOpen}
                                setOpenInfo={setInfoOpen}
                                setSelected={setSelectedType}
                                scores={scores}
                                canScore={
                                    canScore && new Date(item.created_at).toDateString() !== new Date().toDateString()
                                }
                            />
                        );
                    })}
                </Slider>
            </TrackerWrap>
            <Timeline setCanScore={setCanScore} userData={userData} handleDateBlockClick={handleDateBlockClick} />
            <RateModal
                setSelectedData={setSelectedKss}
                open={open}
                onClose={handleScoreChanged}
                selectedType={selectedType}
            />
            {infoOpen ? (
                <FeedbackModal
                    behaviorData={selectedType}
                    open={infoOpen}
                    setIsOpen={setInfoOpen}
                    background={getColorType(selectedType?.status)}
                />
            ) : null}
        </Layout>
    );
};

export default TrackerPage;
